import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AttendanceHistory = ({ attendanceRecords, onRefresh }) => {
  const [filterDate, setFilterDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [filterStaff, setFilterStaff] = useState('all');

  const filteredRecords = attendanceRecords?.filter(record => {
    const recordDate = new Date(record?.check_in_time)?.toISOString()?.split('T')?.[0];
    const matchesDate = recordDate === filterDate;
    const matchesStaff = filterStaff === 'all' || record?.user_id === filterStaff;
    return matchesDate && matchesStaff;
  });

  const uniqueStaff = [...new Set(attendanceRecords?.map(r => r?.user_id))];

  const calculateWorkHours = (record) => {
    if (!record?.check_in_time) return 'N/A';
    
    const checkIn = new Date(record?.check_in_time);
    const checkOut = record?.check_out_time ? new Date(record?.check_out_time) : new Date();
    const totalMinutes = Math.round((checkOut - checkIn) / (1000 * 60));
    const lunchMinutes = record?.lunch_duration_minutes || 0;
    const workMinutes = totalMinutes - lunchMinutes;
    const hours = Math.floor(workMinutes / 60);
    const minutes = workMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="History" size={24} className="text-primary" />
          <h2 className="text-xl font-bold text-foreground">Attendance History</h2>
        </div>
        <Button
          onClick={onRefresh}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Icon name="RefreshCw" size={16} />
          Refresh
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Date
          </label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e?.target?.value)}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Staff Member
          </label>
          <Select
            value={filterStaff}
            onChange={(e) => setFilterStaff(e?.target?.value)}
          >
            <option value="all">All Staff</option>
            {uniqueStaff?.map((userId) => {
              const record = attendanceRecords?.find(r => r?.user_id === userId);
              return (
                <option key={userId} value={userId}>
                  {record?.user_profiles?.full_name}
                </option>
              );
            })}
          </Select>
        </div>
      </div>
      {/* Records Table */}
      <div className="overflow-x-auto">
        {filteredRecords?.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Staff Member</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Check In</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Lunch</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Check Out</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Work Hours</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords?.map((record) => (
                <tr key={record?.id} className="border-b border-border hover:bg-background/50">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-foreground">{record?.user_profiles?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{record?.user_profiles?.email}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {new Date(record?.check_in_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {record?.lunch_start_time ? (
                      <div>
                        <p>{new Date(record?.lunch_start_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        {record?.lunch_duration_minutes && (
                          <p className="text-xs text-muted-foreground">{record?.lunch_duration_minutes} min</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {record?.check_out_time ? (
                      new Date(record?.check_out_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-foreground">
                    {record?.total_hours ? `${record?.total_hours}h` : calculateWorkHours(record)}
                  </td>
                  <td className="py-3 px-4">
                    {record?.check_out_time ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-500/10 text-gray-500">
                        Completed
                      </span>
                    ) : record?.lunch_start_time && !record?.lunch_end_time ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-orange-500/10 text-orange-500">
                        On Lunch
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-500/10 text-green-500">
                        Active
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center text-muted-foreground py-12">
            <Icon name="Calendar" size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No Records Found</p>
            <p className="text-sm mt-2">No attendance records for the selected date and filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceHistory;